import type * as pc from 'playcanvas'
import {
  ensureAssetLoaded,
  getZoneAssetPlan,
  releaseAsset,
} from './assets'

export type AssetZoneStatus = 'inactive' | 'loading' | 'ready' | 'failed' | 'stale'

export interface AssetZoneFailure {
  file: string
  message: string
}

export interface AssetZoneResult {
  zone: string | null
  generation: number
  status: AssetZoneStatus
  loaded: readonly string[]
  failures: readonly AssetZoneFailure[]
}

export interface AssetZoneSnapshot extends AssetZoneResult {}

export interface AssetZoneDriver<Asset> {
  load(file: string): Promise<Asset>
  /** Consumers are torn down before this is called. */
  release(file: string, asset: Asset): void
}

type Consumer<Asset> = {
  mount: (asset: Asset) => void | (() => void)
  cleanup?: () => void
}

type Activation<Asset> = {
  zone: string
  generation: number
  attempt: number
  files: readonly string[]
  assets: Map<string, Asset>
  result?: AssetZoneResult
  promise: Promise<AssetZoneResult>
  settle: (result: AssetZoneResult) => void
}

const MAX_ZONE_LOAD_ATTEMPTS = 2

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function normalizePlan(
  plan: Readonly<Record<string, readonly string[]>>,
): ReadonlyMap<string, readonly string[]> {
  return new Map(Object.entries(plan).map(([zone, files]) => [
    zone,
    [...new Set(files)].sort(),
  ]))
}

/**
 * Owns the single active licensed-asset zone for a PlayCanvas application.
 *
 * PlayCanvas cannot abort a container request once `loadFromUrl` has handed it
 * to the registry. Cancellation is therefore generation based: switching a
 * zone immediately tears down its render instances, and a late completion is
 * never mounted. The late source asset is unloaded as soon as the registry
 * callback returns, unless the new generation also owns the same file.
 */
export class AssetZoneController<Asset> {
  private readonly zones: ReadonlyMap<string, readonly string[]>
  private readonly consumers = new Map<string, Map<string, Set<Consumer<Asset>>>>()
  private active: Activation<Asset> | null = null
  private generation = 0
  private destroyed = false
  private snapshot: AssetZoneSnapshot = {
    zone: null,
    generation: 0,
    status: 'inactive',
    loaded: [],
    failures: [],
  }

  constructor(
    private readonly driver: AssetZoneDriver<Asset>,
    plan: Readonly<Record<string, readonly string[]>>,
  ) {
    this.zones = normalizePlan(plan)
  }

  getSnapshot(): AssetZoneSnapshot {
    return this.snapshot
  }

  getActiveZone(): string | null {
    return this.active?.zone ?? null
  }

  /**
   * Register a render consumer without starting a download. The returned
   * disposer removes that consumer and tears down only its mounted instance.
   */
  register(
    zone: string,
    file: string,
    mount: (asset: Asset) => void | (() => void),
  ): () => void {
    if (this.destroyed) throw new Error('asset zone controller is destroyed')
    const files = this.zones.get(zone)
    if (!files?.includes(file)) {
      throw new Error(`Asset ${file} is not catalogued for zone ${zone}`)
    }

    let byFile = this.consumers.get(zone)
    if (!byFile) {
      byFile = new Map()
      this.consumers.set(zone, byFile)
    }
    let consumers = byFile.get(file)
    if (!consumers) {
      consumers = new Set()
      byFile.set(file, consumers)
    }
    const consumer: Consumer<Asset> = { mount }
    consumers.add(consumer)

    const loaded = this.active?.zone === zone
      ? this.active.assets.get(file)
      : undefined
    if (loaded) {
      const failures: AssetZoneFailure[] = []
      this.mountConsumer(file, consumer, loaded, failures)
      if (failures.length > 0 && this.active?.zone === zone) {
        this.snapshot = {
          ...this.snapshot,
          status: 'failed',
          failures: [...this.snapshot.failures, ...failures],
        }
      }
    }

    return () => {
      this.cleanupConsumer(consumer)
      consumers?.delete(consumer)
      if (consumers?.size === 0) byFile?.delete(file)
      if (byFile?.size === 0) this.consumers.delete(zone)
    }
  }

  /** Switch the active zone. Repeating the same request shares one bounded load cycle. */
  setActiveZone(zone: string | null): Promise<AssetZoneResult> {
    if (this.destroyed) {
      return Promise.reject(new Error('asset zone controller is destroyed'))
    }
    if (zone !== null && !this.zones.has(zone)) {
      return Promise.reject(new Error(`Unknown asset zone: ${zone}`))
    }
    if (this.active?.zone === zone) {
      return this.active.promise
    }

    const nextFiles = new Set(zone === null ? [] : this.zones.get(zone))
    this.generation += 1
    this.teardownActive(nextFiles)

    if (zone === null) {
      const result: AssetZoneResult = {
        zone: null,
        generation: this.generation,
        status: 'inactive',
        loaded: [],
        failures: [],
      }
      this.snapshot = result
      return Promise.resolve(result)
    }

    return this.startActivation(zone, 1)
  }

  private startActivation(zone: string, attempt: number): Promise<AssetZoneResult> {
    const generation = this.generation
    const files = this.zones.get(zone) ?? []
    let resolveActivation!: (result: AssetZoneResult) => void
    const promise = new Promise<AssetZoneResult>((resolve) => {
      resolveActivation = resolve
    })
    const activation: Activation<Asset> = {
      zone,
      generation,
      attempt,
      files,
      assets: new Map(),
      promise,
      settle: (result) => {
        if (activation.result) return
        activation.result = result
        if (this.active === activation) this.snapshot = result
        if (
          result.status === 'failed' &&
          this.active === activation &&
          !this.destroyed &&
          activation.attempt < MAX_ZONE_LOAD_ATTEMPTS
        ) {
          const retry = this.retryActivation(activation)
          void retry.then(resolveActivation)
          return
        }
        resolveActivation(result)
      },
    }
    this.active = activation
    this.snapshot = {
      zone,
      generation,
      status: 'loading',
      loaded: [],
      failures: [],
    }

    void this.loadActivation(activation).then(activation.settle)
    return promise
  }

  private retryActivation(failed: Activation<Asset>): Promise<AssetZoneResult> {
    if (this.active !== failed || this.destroyed) return failed.promise
    this.generation += 1
    // A retry is a clean generation. Partial mounts and loaded assets from the
    // failed attempt must not survive into it or conceal a repeatable failure.
    this.teardownActive(new Set())
    return this.startActivation(failed.zone, failed.attempt + 1)
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.generation += 1
    this.teardownActive(new Set())
    for (const byFile of this.consumers.values()) {
      for (const consumers of byFile.values()) {
        for (const consumer of consumers) this.cleanupConsumer(consumer)
      }
    }
    this.consumers.clear()
    this.snapshot = {
      zone: null,
      generation: this.generation,
      status: 'inactive',
      loaded: [],
      failures: [],
    }
  }

  private async loadActivation(
    activation: Activation<Asset>,
  ): Promise<AssetZoneResult> {
    const failures: AssetZoneFailure[] = []
    const loaded: string[] = []

    await Promise.all(activation.files.map(async (file) => {
      try {
        const asset = await this.driver.load(file)
        if (this.active !== activation || this.destroyed) {
          if (!this.active?.files.includes(file)) {
            this.release(file, asset)
          }
          return
        }
        activation.assets.set(file, asset)
        loaded.push(file)
        this.mountFile(activation.zone, file, asset, failures)
      } catch (error) {
        if (this.active === activation && !this.destroyed) {
          failures.push({ file, message: messageFor(error) })
        }
      }
    }))

    if (this.active !== activation || this.destroyed) {
      return {
        zone: activation.zone,
        generation: activation.generation,
        status: 'stale',
        loaded: [],
        failures: [],
      }
    }

    return {
      zone: activation.zone,
      generation: activation.generation,
      status: failures.length > 0 ? 'failed' : 'ready',
      loaded: loaded.sort(),
      failures,
    }
  }

  private mountFile(
    zone: string,
    file: string,
    asset: Asset,
    failures: AssetZoneFailure[],
  ): void {
    const consumers = this.consumers.get(zone)?.get(file)
    if (!consumers) return
    for (const consumer of consumers) {
      this.mountConsumer(file, consumer, asset, failures)
    }
  }

  private mountConsumer(
    file: string,
    consumer: Consumer<Asset>,
    asset: Asset,
    failures: AssetZoneFailure[],
  ): void {
    this.cleanupConsumer(consumer)
    try {
      const cleanup = consumer.mount(asset)
      consumer.cleanup = typeof cleanup === 'function' ? cleanup : undefined
    } catch (error) {
      failures.push({ file, message: `mount failed: ${messageFor(error)}` })
    }
  }

  private cleanupConsumer(consumer: Consumer<Asset>): void {
    const cleanup = consumer.cleanup
    consumer.cleanup = undefined
    if (!cleanup) return
    try { cleanup() } catch { /* best-effort teardown before asset unload */ }
  }

  private teardownActive(preserveFiles: ReadonlySet<string>): void {
    const active = this.active
    this.active = null
    if (!active) return
    active.settle({
      zone: active.zone,
      generation: active.generation,
      status: 'stale',
      loaded: [],
      failures: [],
    })

    const byFile = this.consumers.get(active.zone)
    if (byFile) {
      for (const consumers of byFile.values()) {
        for (const consumer of consumers) this.cleanupConsumer(consumer)
      }
    }
    for (const [file, asset] of active.assets) {
      if (!preserveFiles.has(file)) this.release(file, asset)
    }
    active.assets.clear()
  }

  private release(file: string, asset: Asset): void {
    try { this.driver.release(file, asset) } catch { /* teardown remains monotonic */ }
  }
}

export function createAssetZoneController(
  app: pc.Application,
): AssetZoneController<pc.Asset> {
  return new AssetZoneController<pc.Asset>(
    {
      load: (file) => ensureAssetLoaded(app, file),
      release: (file, asset) => { releaseAsset(app, file, asset) },
    },
    getZoneAssetPlan(),
  )
}
